import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SessionInfo, User } from "@techplanner/shared/core";
import { api, ApiError, onUnauthorized } from "./lib/api";
import { forgetPushSubscription } from "./lib/push";
import { keys } from "./lib/queries";

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Vrai si la session n'a pas pu être vérifiée faute de réseau (on garde la dernière connue). */
  offline: boolean;
  signIn: (user: User) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);
const LAST_USER_KEY = "tp:last-user";

function lastKnownUser(): User | null {
  try {
    const raw = localStorage.getItem(LAST_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const session = useQuery({
    queryKey: keys.session,
    queryFn: async () => {
      try {
        const res = await api<SessionInfo>("/auth/session");
        localStorage.setItem(LAST_USER_KEY, JSON.stringify(res.user));
        return { user: res.user as User | null, offline: false };
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          localStorage.removeItem(LAST_USER_KEY);
          return { user: null, offline: false };
        }
        // Hors connexion : on laisse travailler avec la dernière session connue.
        const last = lastKnownUser();
        if (last && err instanceof ApiError && (err.isNetwork || err.status >= 500)) return { user: last, offline: true };
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false
  });

  useEffect(
    () =>
      onUnauthorized(() => {
        localStorage.removeItem(LAST_USER_KEY);
        qc.setQueryData(keys.session, { user: null, offline: false });
      }),
    [qc]
  );

  const value: AuthState = {
    user: session.data?.user ?? null,
    loading: session.isPending,
    offline: session.data?.offline ?? false,
    signIn(user) {
      localStorage.setItem(LAST_USER_KEY, JSON.stringify(user));
      qc.setQueryData(keys.session, { user, offline: false });
    },
    async signOut() {
      try {
        // Ce téléphone ne reçoit plus les notifications du compte qui se déconnecte.
        await forgetPushSubscription();
        await api("/auth/logout", { method: "POST" });
      } finally {
        localStorage.removeItem(LAST_USER_KEY);
        qc.clear();
        qc.setQueryData(keys.session, { user: null, offline: false });
      }
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors de AuthProvider");
  return ctx;
}

/** Utilisateur connecté (les pages protégées ne sont rendues qu'avec une session). */
export function useMe(): User {
  const { user } = useAuth();
  if (!user) throw new Error("Session absente");
  return user;
}
